import { routes } from '../../../common/contants/Routes';
import { isGuideDisabled } from '../helpers/guideDisableHelper';
import {
  applyHostColorSchemeToIframeElement,
  getHostColorScheme,
  startHostColorSchemeObserver
} from '../helpers/hostColorSchemeHelper';
import Overlay from './Overlay';
import { MessageTypes } from '../types/MessageTypes';

class Container {
  private static instance: Container | null = null;
  private container: HTMLIFrameElement | null = null;
  private companyId: string | null = null;
  private fadeOutTimeout: NodeJS.Timeout | null = null;
  private isAnimating = false;
  private stopColorSchemeObserver: (() => void) | null = null;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): Container {
    if (!Container.instance) {
      Container.instance = new Container();
    }
    return Container.instance;
  }

  private initialize(): void {
    const scriptElement = document.querySelector('script[gd-company-id]');
    this.companyId = scriptElement?.getAttribute('gd-company-id') || null;
    this.container = document.createElement('iframe');
    const baseUrl = import.meta.env.VITE_TOUR_GUIDE_FRONTEND_URL;

    const iframeUrl = new URL(`${baseUrl}${routes.GUIDE_PLAYER}/${this.companyId}`);
    const parentParams = new URLSearchParams(window.location.search);
    if (parentParams.get('guideLog') === 'true') {
      iframeUrl.searchParams.set('guideLog', 'true');
    }
    const gdToken = parentParams.get('gdToken');
    if (gdToken) {
      iframeUrl.searchParams.set('gdToken', gdToken);
    }
    if (isGuideDisabled()) {
      iframeUrl.searchParams.set('guideDisable', 'true');
    }
    // Chrome force-paints an opaque canvas on the iframe whenever the iframe's
    // declared color-scheme doesn't match the host page's. Forwarding the host's
    // computed color-scheme (mirrored inside the iframe via ChecklistPlayer /
    // GuideProvider) keeps them aligned for every host, including app.getdemo.com.br
    // which doesn't declare color-scheme at all.
    const hostColorScheme = getHostColorScheme();
    iframeUrl.searchParams.set('hostColorScheme', hostColorScheme);

    this.container.src = iframeUrl.toString();
    // The overlay is a cross-origin child, and the Permissions-Policy default
    // allowlist for `autoplay` is `self` — without this the step narration's
    // play() rejects with NotAllowedError and no audio ever starts, since a
    // user gesture on the host page does not propagate into this frame.
    this.container.setAttribute('allow', 'autoplay');
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.container.style.setProperty('position', 'fixed');
    this.container.style.setProperty('border', 'none');
    this.container.style.setProperty('background-color', 'transparent');
    this.container.style.setProperty('z-index', '2147483647');
    this.container.style.setProperty('width', '0');
    this.container.style.setProperty('height', '0');
    this.container.style.setProperty('top', '0');
    this.container.style.setProperty('left', '0');
    this.container.style.setProperty('opacity', '0');
    this.container.style.setProperty('display', 'none');
    this.container.style.setProperty('transition', 'opacity 0.5s ease-in-out');

    this.setupListeners();
    this.stopColorSchemeObserver = startHostColorSchemeObserver(scheme =>
      this.syncHostColorScheme(scheme)
    );
    document.documentElement.appendChild(this.container);
  }

  private syncHostColorScheme(hostColorScheme: string): void {
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.sendMessage({
      type: MessageTypes.HOST_COLOR_SCHEME_CHANGED,
      hostColorScheme
    });
  }

  private setupListeners(): void {
    if (!this.container) return;

    this.container.addEventListener('load', () => {
      this.sendSavedUserData();
      this.syncHostColorScheme(getHostColorScheme());
    });
  }

  private sendSavedUserData(): void {
    const savedUserData = localStorage.getItem('getdemo_user');
    if (savedUserData) {
      try {
        const userData = JSON.parse(savedUserData);
        this.sendMessage({
          type: MessageTypes.IDENTIFY_USER,
          userData
        });
      } catch (error) {
        console.error('Error processing saved user data:', error);
      }
    }
  }

  public sendMessage(message: any): void {
    if (this.container?.contentWindow) {
      this.container.contentWindow.postMessage(message, '*');
    }
  }

  public getContainer(): HTMLIFrameElement | null {
    return this.container;
  }

  public resizeAndPosition(
    position: { top: string; left: string } | undefined,
    dimensions: { width: string; height: string },
    show?: boolean
  ): void {
    if (!this.container) return;

    const isUsableCssValue = (v: unknown): v is string =>
      typeof v === 'string' && v.length > 0 && !v.includes('NaN');

    if (!isUsableCssValue(dimensions?.width) || !isUsableCssValue(dimensions?.height)) {
      console.warn('[Container] resizeAndPosition: invalid dimensions, ignoring', {
        position,
        dimensions
      });
      return;
    }

    if (position) {
      if (!isUsableCssValue(position.top) || !isUsableCssValue(position.left)) {
        console.warn('[Container] resizeAndPosition: invalid position, ignoring position only', {
          position
        });
      } else {
        this.container.style.setProperty('top', position.top);
        this.container.style.setProperty('left', position.left);
      }
    }

    this.container.style.setProperty('width', dimensions.width);
    this.container.style.setProperty('height', dimensions.height);
    if (show) {
      Overlay.getInstance().hide();
      this.show();
    }
  }

  public show(): void {
    if (!this.container) return;

    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }

    this.container.style.setProperty('display', 'block');

    if (!this.isAnimating) {
      this.isAnimating = true;

      this.container.style.setProperty('opacity', '0');

      this.container.offsetHeight;

      this.container.style.setProperty('opacity', '1');

      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    } else {
      this.container.style.setProperty('opacity', '1');
    }
  }

  public hide(): void {
    if (!this.container) return;

    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
    }

    this.container.style.setProperty('opacity', '0');

    this.fadeOutTimeout = setTimeout(() => {
      if (this.container) {
        this.container.style.setProperty('display', 'none');
      }
      this.fadeOutTimeout = null;
    }, 500);
  }

  public destroy(): void {
    if (this.stopColorSchemeObserver) {
      this.stopColorSchemeObserver();
      this.stopColorSchemeObserver = null;
    }

    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }

    if (this.container && document.documentElement.contains(this.container)) {
      document.documentElement.removeChild(this.container);
      this.container = null;
    }
    Container.instance = null;
  }

  public static destroy(): void {
    if (Container.instance) {
      Container.instance.destroy();
    }
  }
}

export default Container;
